import React, { useEffect, useState } from 'react';
import { Users, CheckCircle, XCircle, CreditCard, Search, Download, Loader2, Mail, Phone, Calendar, Trash2, Receipt, X, ShoppingCart, MailCheck } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TabBtn = ({ active, onClick, label, count, color, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition border ${
      active ? `bg-[${color}] text-white border-[${color}]` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
    }`}
    style={active ? { backgroundColor: color, borderColor: color, color: '#fff' } : {}}
  >
    {label}
    <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-700'}`}>{count}</span>
  </button>
);

const TrialReport = () => {
  const [report, setReport] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Honor ?tab=abandoned so the sidebar "Abandoned Carts" link lands on the right tab
  const initialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      return ['active', 'expired', 'paid', 'abandoned'].includes(q) ? q : 'active';
    } catch { return 'active'; }
  })();
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const token = localStorage.getItem('token');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [trialsRes, cartsRes] = await Promise.all([
        axios.get(`${API}/api/public/trial-signups-report`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/public/abandoned-carts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setReport(trialsRes.data);
      setCartData(cartsRes.data);
    } catch (e) {
      console.error('Trial report error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Delete trial "${tenant.name}"? This will remove the tenant and all linked data. This cannot be undone.`)) return;
    setDeletingId(tenant.id);
    try {
      await axios.delete(`${API}/api/public/trial/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchReport();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewReceipt = async (tenant) => {
    setReceiptLoading(true);
    setReceipt({ loading: true, tenant });
    try {
      const res = await axios.get(`${API}/api/public/payment-receipt/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipt(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load receipt');
      setReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const lists = {
    active: (report?.trials || []).filter(t => !t.is_expired),
    expired: (report?.trials || []).filter(t => t.is_expired),
    paid: report?.paid_signups || [],
    abandoned: (cartData?.carts || []).filter(c => !c.recovered),
  };

  const filtered = tab === 'abandoned'
    ? lists.abandoned.filter(c =>
        !search || (c.institute_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.subdomain || '').toLowerCase().includes(search.toLowerCase())
      )
    : lists[tab].filter(t =>
        !search || (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.subdomain || '').toLowerCase().includes(search.toLowerCase())
      );

  const exportCsv = () => {
    if (!filtered.length) return;
    const headers = ['Institute', 'Subdomain', 'Admin Name', 'Email', 'Phone', 'Created', tab === 'expired' || tab === 'active' ? 'Trial Ends' : 'Plan / Payment ID'];
    const rows = filtered.map(t => [
      t.name || '',
      t.subdomain || '',
      t.admin_name || '',
      t.email || '',
      t.phone || '',
      (t.created_at || '').slice(0, 10),
      tab === 'paid' ? (t.plan || t.payment_id || '') : (t.trial_ends || '')
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c + '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trial-signups-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0a1628]" size={28} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="trial-report-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0a1628] flex items-center gap-2"><Users size={26} /> Trial & Purchase Signups Report</h1>
          <p className="text-sm text-gray-500 mt-1">All trial signups and paid purchases from the public landing page.</p>
        </div>
        <button onClick={exportCsv} className="px-4 py-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white rounded-lg text-sm font-medium flex items-center gap-2" data-testid="export-csv-btn">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 text-xs"><Users size={14} /> Total Trials</div>
          <div className="text-2xl font-bold text-[#0a1628] mt-1">{report?.total_trials ?? 0}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 text-green-700 text-xs"><CheckCircle size={14} /> Active Trials</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{lists.active.length}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 text-red-600 text-xs"><XCircle size={14} /> Expired Trials</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{lists.expired.length}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 text-[#0a1628] text-xs"><CreditCard size={14} /> Paid Signups</div>
          <div className="text-2xl font-bold text-[#0a1628] mt-1">{lists.paid.length}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 text-amber-600 text-xs"><ShoppingCart size={14} /> Abandoned · Recovery {cartData?.recovery_rate ?? 0}%</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{lists.abandoned.length}</div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          <TabBtn active={tab === 'active'} onClick={() => setTab('active')} label="Active Trials" count={lists.active.length} color="#16a34a" testid="tab-active" />
          <TabBtn active={tab === 'expired'} onClick={() => setTab('expired')} label="Expired Trials" count={lists.expired.length} color="#dc2626" testid="tab-expired" />
          <TabBtn active={tab === 'paid'} onClick={() => setTab('paid')} label="Paid Signups" count={lists.paid.length} color="#0a1628" testid="tab-paid" />
          <TabBtn active={tab === 'abandoned'} onClick={() => setTab('abandoned')} label="Abandoned Carts" count={lists.abandoned.length} color="#f59e0b" testid="tab-abandoned" />
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name / email / subdomain..."
            className="pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#0a1628] outline-none w-72"
            data-testid="trial-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">No records.</div>
        ) : tab === 'abandoned' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3">Institute</th>
                  <th className="px-4 py-3">Admin</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Plan Selected</th>
                  <th className="px-4 py-3">Age</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50" data-testid={`cart-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0a1628]">{c.institute_name || '(not filled)'}</div>
                      <div className="text-xs text-gray-500">{c.subdomain ? `cloudcampus.site/${c.subdomain}` : '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{c.admin_name || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600"><Mail size={12} /> {c.email}</div>
                      {c.phone && <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5"><Phone size={12} /> {c.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {c.plan ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold capitalize">{c.plan} · {c.billing_cycle || 'monthly'}</span>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {c.age_hours != null ? (c.age_hours < 24 ? `${Math.round(c.age_hours)}h` : `${Math.round(c.age_hours / 24)}d`) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const step = c.reminder_step || 0;
                        if (step === 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">Pending</span>;
                        const label = step === 1 ? '24h sent' : step === 2 ? '72h sent' : 'Final sent';
                        const color = step === 1 ? 'bg-amber-50 text-amber-700' : step === 2 ? 'bg-orange-50 text-orange-700' : 'bg-purple-50 text-purple-700';
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
                            <MailCheck size={12} /> {label} ({step}/3)
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3">Institute</th>
                  <th className="px-4 py-3">Admin</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">{tab === 'paid' ? 'Plan' : 'Trial Ends'}</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((t, i) => (
                  <tr key={i} className="hover:bg-gray-50" data-testid={`trial-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0a1628]">{t.name}</div>
                      <div className="text-xs text-gray-500">cloudcampus.site/{t.subdomain}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{t.admin_name || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600"><Mail size={12} /> {t.email}</div>
                      {t.phone && <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5"><Phone size={12} /> {t.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1"><Calendar size={12} />{(t.created_at || '').slice(0, 10)}</div>
                    </td>
                    <td className="px-4 py-3">
                      {tab === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold capitalize">{t.plan || 'paid'}</span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${t.is_expired ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                          {t.trial_ends}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a
                          href={`https://cloudcampus.site/${t.subdomain}/login`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#0a1628] hover:underline font-medium"
                          data-testid={`open-${i}`}
                        >Open ↗</a>
                        {tab === 'paid' && (
                          <button
                            onClick={() => handleViewReceipt(t)}
                            className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1"
                            data-testid={`receipt-${i}`}
                          ><Receipt size={12} /> Receipt</button>
                        )}
                        {tab !== 'paid' && (
                          <button
                            onClick={() => handleDelete(t)}
                            disabled={deletingId === t.id}
                            className="text-xs text-red-600 hover:underline font-medium flex items-center gap-1 disabled:opacity-50"
                            data-testid={`delete-${i}`}
                          >{deletingId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {receipt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReceipt(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="receipt-modal">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-[#0a1628]">Payment Receipts</h2>
                <p className="text-xs text-gray-500">{receipt.tenant?.name || receipt.loading ? '' : 'Loading...'}</p>
              </div>
              <button onClick={() => setReceipt(null)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-receipt"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              {receiptLoading ? (
                <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-[#0a1628]" size={28} /></div>
              ) : (
                <>
                  {receipt.tenant && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-[#0a1628] mb-2">{receipt.tenant.name}</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div><span className="text-gray-500">Email:</span> {receipt.tenant.admin_email}</div>
                        <div><span className="text-gray-500">Phone:</span> {receipt.tenant.phone || '-'}</div>
                        <div><span className="text-gray-500">GST Number:</span> <span className="font-mono">{receipt.tenant.gst_number || '—'}</span></div>
                        <div><span className="text-gray-500">State:</span> {receipt.tenant.billing_state || '—'}</div>
                        {receipt.tenant.billing_address && <div className="col-span-2"><span className="text-gray-500">Billing Address:</span> {receipt.tenant.billing_address}, {receipt.tenant.billing_city}, {receipt.tenant.billing_state} - {receipt.tenant.billing_pincode}</div>}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-sm text-[#0a1628] mb-2">Invoices ({receipt.invoices?.length || 0})</h4>
                    {(receipt.invoices || []).length === 0 ? <p className="text-xs text-gray-400">No invoices.</p> : (
                      <div className="space-y-2">
                        {receipt.invoices.map((inv, i) => (
                          <div key={i} className="border border-gray-200 rounded-lg p-3 text-xs">
                            <div className="flex justify-between">
                              <span className="font-medium text-[#0a1628]">{inv.invoice_number || inv.id?.slice(0, 8)}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{inv.status}</span>
                            </div>
                            <div className="mt-1 text-gray-600">Plan: {inv.plan_code} ({inv.billing_cycle}) | Amount: ₹{(inv.amount || 0).toLocaleString('en-IN')}</div>
                            {inv.invoice_date && <div className="text-gray-400">Date: {inv.invoice_date}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm text-[#0a1628] mb-2">Subscription Payments ({receipt.subscription_payments?.length || 0})</h4>
                    {(receipt.subscription_payments || []).length === 0 ? <p className="text-xs text-gray-400">No subscription payments.</p> : (
                      <div className="space-y-2">
                        {receipt.subscription_payments.map((p, i) => (
                          <div key={i} className="border border-gray-200 rounded-lg p-3 text-xs">
                            <div className="flex justify-between">
                              <span className="font-medium text-[#0a1628]">{p.razorpay_payment_id || p.payment_id || p.id?.slice(0, 8)}</span>
                              <span className="text-green-600 font-semibold">₹{(p.amount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="mt-1 text-gray-600">Plan: {p.plan_code || p.plan} | Cycle: {p.billing_cycle}</div>
                            <div className="text-gray-400">Date: {(p.created_at || p.payment_date || '').slice(0, 10)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialReport;
